import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { GatewayOutage } from "@/contracts";

import { acknowledgeGatewayOutageMutation } from "@/api/queries";
import { Alert } from "@/components/ui/alert";
import { TextAreaField } from "@/components/ui/field";
import { FormDialog } from "@/components/ui/form-dialog";
import { useApiClient } from "@/lib/api-context";
import { formatDateTime } from "@/lib/format";

const NOTE_MAX = 500;

/**
 * Taking a gateway outage on. Like an incident, it closes when the gateway
 * reconnects, never because somebody acknowledged it.
 */
export function AcknowledgeOutageDialog({
  outage,
  onClose,
}: {
  outage: GatewayOutage;
  onClose: () => void;
}) {
  const { t } = useTranslation("pages");
  const client = useApiClient();
  const queryClient = useQueryClient();
  const [note, setNote] = useState("");

  const mutation = useMutation({
    ...acknowledgeGatewayOutageMutation(client),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["gateways", "outages"] });
    },
  });

  const tooLong = note.trim().length > NOTE_MAX;

  return (
    <FormDialog
      open
      title={t("dialogAcknowledgeOutageTitle")}
      description={t("dialogAcknowledgeOutageDescription", {
        name: outage.gatewayName,
        since: formatDateTime(outage.startedAt),
      })}
      submitLabel={t("acknowledge")}
      pending={mutation.isPending}
      onSubmit={(event) => {
        event.preventDefault();

        if (tooLong) {
          return;
        }

        const trimmed = note.trim();
        void mutation
          .mutateAsync({ id: outage.id, body: trimmed ? { note: trimmed } : {} })
          .then(onClose)
          .catch(() => undefined);
      }}
      onCancel={onClose}
    >
      <div className="flex flex-col gap-4">
        {mutation.isError ? <Alert>{mutation.error.message}</Alert> : null}

        <Alert tone="info" title={t("dialogAcknowledgeWhatTitle")} announce={false}>
          {t("dialogAcknowledgeOutageWhatBody")}
        </Alert>

        <TextAreaField
          label={t("fieldNote")}
          hint={t("acknowledgeNoteHint")}
          value={note}
          onChange={(event) => setNote(event.target.value)}
          {...(tooLong ? { error: t("noteTooLong", { max: NOTE_MAX }) } : {})}
        />
      </div>
    </FormDialog>
  );
}
